import { DynamicFormRequiredType } from '@myosh/odin-components';
import type {
  CheckboxChangeEventType,
  DropDownResult,
  DynamicFormSettings,
  JsonData,
  JsonDataItem,
  VisibilityCondition,
} from '@myosh/odin-components';
import { ModuleFields } from '../../../utils/module-config';
import i18next from '../../../i18n';
import HfDropDown from '../../fields/custom-drop-downs/hf-drop-down.component';
import HfDropDownMultiSelect from '../../fields/custom-drop-downs/hf-drop-down-multi-select.component';
import { forceAssert } from '../../../utils/type-guards';

export const getModuleTypeData = (value?: string): JsonData => {
  const data = [
    {
      text: i18next.t('module'),
      value: 'MODULE',
    },
    {
      text: i18next.t('embedded-url'),
      value: 'EMBEDDED_URL',
    },
  ];

  if (value) {
    const foundData = data.find((item) => item.value === value);
    return foundData ? [foundData] : [];
  }

  return data;
};

export const getModuleTargetData = (value?: string): JsonData => {
  const data = [
    {
      text: i18next.t('current-window'),
      value: 'CURRENT_WINDOW',
    },
    {
      text: i18next.t('open-in-new-window'),
      value: 'NEW_WINDOW',
    },
  ];

  if (value) {
    const foundData = data.find((item) => item.value === value);
    return foundData ? [foundData] : [];
  }

  return data;
};

const targetVisibilityCondition: VisibilityCondition = {
  id: 'typeVisibilityCondition',
  sourceFieldId: ModuleFields.type,
  fieldVisibilityType: 'FIELD',
  targetValueType: 'STRING',
  targetValue: i18next.t('embedded-url'),
  targetValueId: 'EMBEDDED_URL',
};

export const getModuleFormSettings = (): DynamicFormSettings => {
  return {
    id: 0,
    caption: '',
    fields: [
      {
        id: 100,
        fieldType: 'GROUP',
        fieldName: 'Module Details',
        caption: i18next.t('module-details'),
        position: 0,
        startOpen: true,
        permissions: { read: true, edit: true },
        fields: [
          {
            id: ModuleFields.caption,
            fieldType: 'TEXTFIELD',
            fieldName: ModuleFields.caption,
            caption: i18next.t('name'),
            required: DynamicFormRequiredType.True,
            position: 0,
          },
          {
            id: ModuleFields.viewId,
            fieldType: 'TEXTFIELD',
            fieldName: ModuleFields.viewId,
            caption: i18next.t('view-id'),
            required: DynamicFormRequiredType.True,
            position: 1,
          },
          {
            id: ModuleFields.moduleGroupId,
            fieldType: 'COMBOBOX',
            fieldName: ModuleFields.moduleGroupId,
            caption: i18next.t('module-group'),
            dataSettings: {
              valueField: 'value',
              textField: 'text',
            },
            required: DynamicFormRequiredType.True,
            position: 2,
          },
          {
            id: ModuleFields.type,
            fieldType: 'OPTIONGROUP',
            fieldName: ModuleFields.type,
            caption: i18next.t('module-type'),
            dataSettings: {
              textField: 'text',
              valueField: 'value',
              data: getModuleTypeData(),
            },
            required: DynamicFormRequiredType.True,
            position: 3,
          },
          {
            id: ModuleFields.sourceUrl,
            fieldType: 'TEXTFIELD',
            fieldName: ModuleFields.sourceUrl,
            caption: i18next.t('source-url'),
            required: DynamicFormRequiredType.True,
            position: 4,
          },
          {
            id: ModuleFields.target,
            fieldType: 'OPTIONGROUP',
            fieldName: ModuleFields.target,
            caption: i18next.t('target'),
            dataSettings: {
              textField: 'text',
              valueField: 'value',
              data: getModuleTargetData(),
            },
            required: DynamicFormRequiredType.True,
            position: 5,
          },
          {
            id: ModuleFields.displayInMobile,
            fieldType: 'CHECKBOX',
            fieldName: ModuleFields.displayInMobile,
            caption: i18next.t('display-in-mobile'),
            required: DynamicFormRequiredType.False,
            position: 6,
          },
          {
            id: ModuleFields.hidden,
            fieldType: 'CHECKBOX',
            fieldName: ModuleFields.hidden,
            caption: i18next.t('hidden'),
            required: DynamicFormRequiredType.False,
            position: 7,
          },

          {
            id: ModuleFields.showAllFormsFirst,
            fieldType: 'CHECKBOX',
            fieldName: ModuleFields.showAllFormsFirst,
            caption: i18next.t('show-all-forms-first'),
            required: DynamicFormRequiredType.False,
            position: 8,
          },
          {
            id: ModuleFields.icon,
            fieldType: 'ICON_SELECTOR',
            fieldName: ModuleFields.icon,
            caption: i18next.t('icon'),
            required: DynamicFormRequiredType.False,
            position: 9,
          },
          {
            id: ModuleFields.roles,
            fieldType: 'DROPDOWNMULTISELECT',
            fieldName: ModuleFields.roles,
            caption: i18next.t('module-roles'),
            dataSettings: {
              valueField: 'id',
              textField: 'caption',
            },
            required: DynamicFormRequiredType.True,
            position: 10,
          },
        ],
      },
    ],
    visibilityConditions: [
      {
        id: ModuleFields.target,
        type: 'OPTIONGROUP',
        groups: [
          {
            conditions: [targetVisibilityCondition],
          },
        ],
      },
      {
        id: ModuleFields.sourceUrl,
        type: 'TEXTFIELD',
        groups: [
          {
            conditions: [targetVisibilityCondition],
          },
        ],
      },
      {
        id: ModuleFields.displayInMobile,
        type: 'CHECKBOX',
        groups: [
          {
            conditions: [targetVisibilityCondition],
          },
        ],
      },
    ],
    fieldValueConverters: {
      CHECKBOX: (value) => {
        const convertedValue = forceAssert<CheckboxChangeEventType>(value);
        return convertedValue.checked;
      },
    },
    customFieldComponents: {
      COMBOBOX: HfDropDown,
      DROPDOWNMULTISELECT: HfDropDownMultiSelect,
    },
  };
};

export const formatFormData = (formData: JsonDataItem) => {
  formData.moduleGroupId = forceAssert<DropDownResult>(formData.moduleGroupId).value;
  formData.type = forceAssert<DropDownResult>(formData.type).value;

  if (formData.target) {
    formData.target = forceAssert<DropDownResult>(formData.target).value;
  }

  formData.roles = forceAssert<Array<DropDownResult>>(formData.roles).map((role) => {
    return {
      id: role.value,
    };
  });
  formData.allFormConfigurationPermissionDTO = {
    groupsAbleToCreateGlobalViews: [],
  };
  formData.position = 1;

  return formData;
};
